import { api } from "./client";
import { unwrapApiData, unwrapApiList } from "./unwrap";

/** crm_party.party_type (0-based) — mirrors backend. */
export const PartyType = {
  CUSTOMER: 0,
  VENDOR: 1,
  TENANT: 2,
  PATIENT: 3,
  LEAD: 4,
  CONTRACTOR: 5,
  EMPLOYEE_CONTACT: 6,
  SHIPPING_CARRIER: 7,
} as const;

export type PartyTypeCode = (typeof PartyType)[keyof typeof PartyType];

/** crm_interaction.channel_code (0-based). */
export const InteractionChannel = {
  CALL: 0,
  EMAIL: 1,
  MEETING: 2,
  VISIT: 3,
  NOTE: 4,
  WHATSAPP: 5,
} as const;

export type InteractionChannelCode =
  (typeof InteractionChannel)[keyof typeof InteractionChannel];

export interface CrmParty {
  partyId: string;
  orgId: string;
  partyType: PartyTypeCode;
  displayName: string;
  legalName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  locale: string | null;
  taxId: string | null;
  countryCode: string;
  verticalAttributes: Record<string, unknown>;
  status: number;
  ownerUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CrmInteraction {
  interactionId: string;
  channelCode: InteractionChannelCode;
  subject: string | null;
  body: string | null;
  createdBy: string | null;
  at: string;
}

export interface CreatePartyPayload {
  displayName: string;
  partyType: PartyTypeCode;
  primaryEmail?: string;
  primaryPhone?: string;
  taxId?: string;
  legalName?: string;
  countryCode?: string;
  verticalCode?: number;
  locale?: string;
}

export interface FetchPartiesQuery {
  status?: number;
  startDate?: string;
  endDate?: string;
}

export interface FetchInteractionsQuery {
  startDate?: string;
  endDate?: string;
}

export interface AddInteractionPayload {
  channel: InteractionChannelCode;
  interactionDate: string;
  notes?: string;
  subject?: string;
}

function buildQuery(params?: Record<string, string | undefined>): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, value);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export async function fetchParties(
  query?: FetchPartiesQuery,
): Promise<CrmParty[]> {
  const res = await api.get<
    { ok: true; data: CrmParty[] } | import("./types").ApiErrorBody
  >(
    `/v1/crm/parties${buildQuery({
      status: query?.status !== undefined ? String(query.status) : undefined,
      startDate: query?.startDate,
      endDate: query?.endDate,
    })}`,
  );

  const parties = unwrapApiList(res);

  return [...parties].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }),
  );
}

export async function fetchParty(partyId: string): Promise<CrmParty> {
  const res = await api.get<
    { ok: true; data: CrmParty } | import("./types").ApiErrorBody
  >(`/v1/crm/parties/${encodeURIComponent(partyId)}`);

  return unwrapApiData(res);
}

export async function createParty(payload: CreatePartyPayload): Promise<CrmParty> {
  const res = await api.post<
    { ok: true; data: CrmParty } | import("./types").ApiErrorBody
  >("/v1/crm/parties", {
    displayName: payload.displayName.trim(),
    partyType: payload.partyType,
    primaryEmail: payload.primaryEmail?.trim() || undefined,
    primaryPhone: payload.primaryPhone?.trim() || undefined,
    taxId: payload.taxId?.trim() || undefined,
    legalName: payload.legalName?.trim() || undefined,
    countryCode: payload.countryCode?.trim() || undefined,
    verticalCode: payload.verticalCode,
    locale: payload.locale,
  });

  return unwrapApiData(res);
}

export async function fetchPartyInteractions(
  partyId: string,
  query?: FetchInteractionsQuery,
): Promise<CrmInteraction[]> {
  const res = await api.get<
    { ok: true; data: CrmInteraction[] } | import("./types").ApiErrorBody
  >(
    `/v1/crm/parties/${encodeURIComponent(partyId)}/interactions${buildQuery({
      startDate: query?.startDate,
      endDate: query?.endDate,
    })}`,
  );

  return unwrapApiList(res);
}

export interface UpdatePartyPayload {
  displayName?: string;
  partyType?: PartyTypeCode;
  primaryEmail?: string;
  primaryPhone?: string;
  taxId?: string;
  legalName?: string;
  verticalAttributes?: Record<string, unknown>;
  status?: number;
}

export async function updateParty(
  partyId: string,
  payload: UpdatePartyPayload,
): Promise<CrmParty> {
  const res = await api.patch<
    { ok: true; data: CrmParty } | import("./types").ApiErrorBody
  >(`/v1/crm/parties/${encodeURIComponent(partyId)}`, payload);
  return unwrapApiData(res);
}

export async function deleteParty(partyId: string): Promise<void> {
  await api.delete<unknown>(`/v1/crm/parties/${encodeURIComponent(partyId)}`);
}

export type PartyGridRow = Record<string, string>;

export async function fetchPartyBulkGrid(options?: {
  partyType?: "customer" | "vendor";
}): Promise<{
  headers: string[];
  rows: PartyGridRow[];
  partyTypeFilter?: string;
}> {
  const qs =
    options?.partyType != null
      ? `?partyType=${encodeURIComponent(options.partyType)}`
      : "";
  const res = await api.get<
    | {
        ok: true;
        data: {
          headers: string[];
          rows: PartyGridRow[];
          partyTypeFilter?: string;
        };
      }
    | import("./types").ApiErrorBody
  >(`/v1/crm/parties/bulk-grid${qs}`);
  return unwrapApiData(res);
}

export async function savePartyBulkGrid(
  rows: PartyGridRow[],
  options?: { defaultPartyType?: "customer" | "vendor" },
): Promise<{
  inserted: number;
  updated: number;
  errors: Array<{ row: number; message: string }>;
}> {
  const res = await api.post<
    | {
        ok: true;
        data: {
          inserted: number;
          updated: number;
          errors: Array<{ row: number; message: string }>;
        };
      }
    | import("./types").ApiErrorBody
  >("/v1/crm/parties/bulk-grid/save", {
    rows,
    defaultPartyType: options?.defaultPartyType,
  });
  return unwrapApiData(res);
}

export async function reviewPartyBulkGridAi(rows: PartyGridRow[]): Promise<{
  warnings: string[];
  aiText: string | null;
  aiAvailable: boolean;
  aiModel: string | null;
}> {
  const res = await api.post<
    | {
        ok: true;
        data: {
          warnings: string[];
          aiText: string | null;
          aiAvailable: boolean;
          aiModel: string | null;
        };
      }
    | import("./types").ApiErrorBody
  >("/v1/crm/parties/bulk-grid/ai-review", { rows });
  return unwrapApiData(res);
}

export async function addPartyInteraction(
  partyId: string,
  payload: AddInteractionPayload,
): Promise<CrmInteraction> {
  const res = await api.post<
    { ok: true; data: CrmInteraction } | import("./types").ApiErrorBody
  >(`/v1/crm/parties/${encodeURIComponent(partyId)}/interactions`, {
    channel: payload.channel,
    interactionDate: payload.interactionDate,
    notes: payload.notes?.trim() || undefined,
    subject: payload.subject?.trim() || undefined,
  });

  return unwrapApiData(res);
}
